import { motion } from "framer-motion";
import { ArrowRight, GitCompare, Box, Cable, MessageSquare, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import previewCompare from "@/assets/preview-compare.jpg";
import previewBox from "@/assets/preview-boxbuilder.jpg";
import previewWiring from "@/assets/preview-wiring.jpg";
import previewAI from "@/assets/preview-aichat.jpg";

const features = [
  {
    title: "Product Comparison",
    description: "Compare subwoofers, amps, speakers & tweeters side-by-side across reliability, build quality, and real-world ratings.",
    image: previewCompare,
    icon: GitCompare,
  },
  {
    title: "Box Builder",
    description: "Design sealed, ported, 4th order, 6th order, and flat wall enclosures with exact cut sheets for your vehicle.",
    image: previewBox,
    icon: Box,
  },
  {
    title: "Wiring Diagrams",
    description: "Interactive diagrams for up to 6 subs in any series, parallel, or series-parallel config. See final impedance instantly.",
    image: previewWiring,
    icon: Cable,
  },
  {
    title: "AI Car Audio Expert",
    description: "Ask anything about car audio — from Thiele-Small parameters to the best deadening for your doors. Instant, detailed answers.",
    image: previewAI,
    icon: MessageSquare,
  },
];

const FeatureShowcase = () => {
  const { user } = useAuth();

  return (
    <section className="py-14 sm:py-20 md:py-28 bg-surface-sunken border-t border-b border-border">
      <div className="container px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <p className="section-label text-sm font-mono text-primary uppercase tracking-wider mb-2">
            What You Get
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-foreground mb-3">
            Everything You Need for Car Audio
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Professional-grade tools that take the guesswork out of your build.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group border border-border rounded-sm bg-card overflow-hidden"
            >
              <div className="relative overflow-hidden">
                <img
                  src={f.image}
                  alt={`${f.title} preview`}
                  loading="lazy"
                  width={960}
                  height={640}
                  className="w-full h-48 sm:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
              </div>
              <div className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <f.icon className="w-4 h-4 text-primary" />
                  <h3 className="font-display font-bold text-foreground">{f.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {f.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10 sm:mt-14"
          >
            <Link to="/auth">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-primary-foreground font-semibold text-base rounded-sm shadow-hard hover:brightness-110 transition-all"
              >
                <Zap className="w-4 h-4" />
                Sign Up Free to Get Started
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </motion.button>
            </Link>
            <p className="text-xs text-muted-foreground font-mono mt-3">
              No credit card required · Free tools available immediately
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default FeatureShowcase;
